import { NextRouter } from 'next/router';

/**
 * 安全处理重定向URL，防止反射型XSS攻击
 *
 * 主要用于处理来自redirect_url参数的重定向URL
 * 如果URL不包含域名（相对路径），直接返回
 * 如果包含域名，替换为当前站点域名
 *
 * 使用方式:
 * 1. 客户端重定向: window.location.href = getSafeRedirectUrl(redirectUrl)
 * 2. Next.js路由: router.push(getSafeRedirectUrl(redirectUrl))
 * 3. ?redirect_url=${encodeURIComponent(getSafeRedirectUrl(redirectUrl))}`
 *
 * @param redirectUrl 原始重定向URL
 * @returns 处理后的安全URL
 */
export function getSafeRedirectUrl(redirectUrl: string): string {
  if (!redirectUrl) {
    return '/';
  }

  // 检查是否存在危险的协议（如javascript:）
  if (/^(javascript|data|vbscript|file):/i.test(redirectUrl)) {
    console.error('危险的重定向URL协议:', redirectUrl);
    return '/';
  }

  // 检查URL是否包含协议前缀
  const hasProtocol = /^(https?:)?\/\//.test(redirectUrl);

  // 如果不包含协议前缀，直接返回原URL（相对路径是安全的）确保URL以/开头
  if (!hasProtocol) {
    return redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`;
  }

  // 获取当前站点域名
  const host = process.env.WEB_HOST || process.env.NEXT_PUBLIC_WEB_HOST;
  try {
    // 尝试解析URL
    const url = new URL(redirectUrl.startsWith('//') ? `https:${redirectUrl}` : redirectUrl);

    // 如果URL的域名是 syw.bz，直接返回原始URL (like: http%3A%2F%2Fsyw.bz%2F0B6OCB)
    if (url.hostname === 'syw.bz') {
      return redirectUrl;
    }

    // 如果URL的域名与当前站点不同，替换为当前站点域名
    const originalPath = url.pathname + url.search + url.hash;
    return `${host}${originalPath.startsWith('/') ? originalPath : `/${originalPath}`}`;
  } catch (error) {
    // URL解析失败，返回首页
    console.error('无效的重定向URL:', redirectUrl);
    return '/';
  }
}
